import { Container } from 'pixi.js';

export interface CameraBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

/**
 * Isometric camera with pan, zoom, and bounds clamping.
 * Manipulates a PixiJS container's position and scale.
 */
export class IsoCamera {
  private target: Container;
  private _x: number = 0;
  private _y: number = 0;
  private _zoom: number = 1.0;
  private _minZoom: number = 0.3;
  private _maxZoom: number = 2.0;
  private bounds: CameraBounds | null = null;

  // Smooth animation state
  private targetX: number = 0;
  private targetY: number = 0;
  private targetZoom: number = 1.0;
  private smoothing: number = 0.15; // 0 = instant, 1 = never arrives

  private viewportWidth: number = 800;
  private viewportHeight: number = 600;

  constructor(target: Container) {
    this.target = target;
  }

  /** Set the viewport size (call on resize). */
  setViewport(width: number, height: number): void {
    this.viewportWidth = width;
    this.viewportHeight = height;
  }

  /** Set the camera bounds (world-space pixel extents). */
  setBounds(bounds: CameraBounds): void {
    this.bounds = bounds;
  }

  /** Calculate bounds from a grid size. */
  setBoundsFromGrid(gridWidth: number, gridHeight: number, tileWidth: number, tileHeight: number): void {
    // In isometric 2:1, the grid diamond spans:
    // X: -(gridHeight * tw/2) to +(gridWidth * tw/2)  centered at 0
    // Y: 0 (top corner) to (gridW + gridH) * th/2 (bottom corner)
    // Add margin for walls (extend upward ~60px) and breathing room
    const totalWidth = (gridWidth + gridHeight) * (tileWidth / 2);
    const totalHeight = (gridWidth + gridHeight) * (tileHeight / 2);
    const wallHeight = 80; // Walls and furniture extend above the grid
    const margin = 50;
    this.bounds = {
      minX: -(totalWidth / 2) - margin,
      maxX: (totalWidth / 2) + margin,
      minY: -wallHeight - margin,
      maxY: totalHeight + margin,
    };
  }

  /** Set min/max zoom levels. */
  setZoomLimits(min: number, max: number): void {
    this._minZoom = min;
    this._maxZoom = max;
  }

  /** Calculate the zoom level that fits the entire grid on screen. */
  fitAll(gridWidth: number, gridHeight: number, tileWidth: number, tileHeight: number): number {
    const totalWidth = (gridWidth + gridHeight) * (tileWidth / 2);
    const totalHeight = (gridWidth + gridHeight) * (tileHeight / 2);
    const zoomX = this.viewportWidth / (totalWidth + 100);
    const zoomY = this.viewportHeight / (totalHeight + 100);
    return Math.min(zoomX, zoomY, this._maxZoom);
  }

  /** Pan the camera by a screen-space delta (drag direction = scroll direction). */
  pan(dx: number, dy: number): void {
    this.targetX += dx / this._zoom;
    this.targetY += dy / this._zoom;
    this.clampTarget();
  }

  /** Set camera center position directly (world space). */
  setPosition(x: number, y: number): void {
    this.targetX = x;
    this.targetY = y;
    this.clampTarget();
  }

  /** Jump to position instantly (no smoothing). */
  jumpTo(x: number, y: number): void {
    this.targetX = x;
    this.targetY = y;
    this._x = x;
    this._y = y;
    this.clampTarget();
    this.applyTransform();
  }

  /** Set zoom level with optional focal point (screen coords). */
  setZoom(zoom: number, focalX?: number, focalY?: number): void {
    const oldZoom = this._zoom;
    this.targetZoom = Math.max(this._minZoom, Math.min(this._maxZoom, zoom));

    // Zoom toward focal point
    if (focalX !== undefined && focalY !== undefined) {
      const worldFocalX = (focalX - this.viewportWidth / 2) / oldZoom + this._x;
      const worldFocalY = (focalY - this.viewportHeight / 2) / oldZoom + this._y;
      this.targetX = worldFocalX - (focalX - this.viewportWidth / 2) / this.targetZoom;
      this.targetY = worldFocalY - (focalY - this.viewportHeight / 2) / this.targetZoom;
    }

    this.clampTarget();
  }

  /** Zoom by a relative factor (e.g., 1.1 = zoom in 10%). */
  zoomBy(factor: number, focalX?: number, focalY?: number): void {
    this.setZoom(this.targetZoom * factor, focalX, focalY);
  }

  /** Toggle between fit-all and close-up zoom. */
  toggleZoom(gridWidth: number, gridHeight: number, tileWidth: number, tileHeight: number): void {
    const fitZoom = this.fitAll(gridWidth, gridHeight, tileWidth, tileHeight);
    if (Math.abs(this._zoom - fitZoom) < 0.1) {
      this.setZoom(1.0);
    } else {
      this.setZoom(fitZoom);
    }
  }

  /** Update camera interpolation. Call every frame. */
  update(): void {
    this._x += (this.targetX - this._x) * this.smoothing;
    this._y += (this.targetY - this._y) * this.smoothing;
    this._zoom += (this.targetZoom - this._zoom) * this.smoothing;
    this.applyTransform();
  }

  /** Current camera position (world space). */
  get x(): number { return this._x; }
  get y(): number { return this._y; }
  get zoom(): number { return this._zoom; }

  /** Convert screen coordinates to world coordinates (accounting for camera). */
  screenToCamera(screenX: number, screenY: number): { x: number; y: number } {
    return {
      x: (screenX - this.viewportWidth / 2) / this._zoom + this._x,
      y: (screenY - this.viewportHeight / 2) / this._zoom + this._y,
    };
  }

  private clampTarget(): void {
    if (!this.bounds) return;
    const halfViewW = (this.viewportWidth / 2) / this.targetZoom;
    const halfViewH = (this.viewportHeight / 2) / this.targetZoom;
    this.targetX = Math.max(this.bounds.minX + halfViewW, Math.min(this.bounds.maxX - halfViewW, this.targetX));
    this.targetY = Math.max(this.bounds.minY + halfViewH, Math.min(this.bounds.maxY - halfViewH, this.targetY));
  }

  private applyTransform(): void {
    this.target.scale.set(this._zoom);
    this.target.position.set(
      this.viewportWidth / 2 - this._x * this._zoom,
      this.viewportHeight / 2 - this._y * this._zoom,
    );
  }
}
